import * as React from "react";
import { Button } from "@/components/ui";
import { GUIDE_LINKS } from "./guideLinks";
import { HelpCallout, HelpCards, HelpDialog, HelpDialogProps, HelpFaq, HelpFooter } from "./HelpDialog";
import { HowBackgroundsWorkDialog } from "./HowBackgroundsWorkDialog";
import { WhatCanIAskDialog } from "./WhatCanIAskDialog";

const STEPS = [
  {
    title: "Describe it",
    body: "Say what the post is about and the look you want, or start from a SocialCoach template. You choose the layout, type, and photo, and Design Studio builds it to your brief.",
  },
  {
    title: "Refine it",
    body: 'Ask for changes in plain words, like "make the quote bigger". Compare before and after, and keep the version you like.',
  },
  {
    title: "Publish it",
    body: "Publishing adds the design to your prompt library as a Personalized prompt. Choose who gets it, the same way as any prompt.",
  },
  {
    title: "Players share it",
    body: "When a player shares a review, their post is built from your design with their name, headshot, review, and branch branding. No AI runs at that point.",
  },
];

const FAQ = [
  {
    q: "Can I change a design after publishing?",
    a: "Yes. Refine it and keep the new version; players get it on their next post. Posts already made don't change.",
  },
  {
    q: "Who can use Design Studio?",
    a: "Coaches, and assistants with the Design Studio permission. Designs built around a review also need Reputation Manager.",
  },
  {
    q: "How long does it take?",
    a: "Creating or changing a design can take a couple of minutes, and you can leave the page while it works. Previews and your players' posts are ready in under a minute.",
  },
  {
    q: "Can two people work on the same design?",
    a: "Yes. You'll see who's viewing, and every change shows up for everyone as it happens.",
  },
];

export const DesignStudioHelpDialog: React.FC<HelpDialogProps> = ({ open, onOpenChange }) => {
  const [linked, setLinked] = React.useState<"ask" | "backgrounds" | null>(null);
  const closeLinked = (next: boolean) => !next && setLinked(null);
  return (
    <>
      <HelpDialog
        open={open}
        onOpenChange={onOpenChange}
        title="How Design Studio works"
        showLogo={false}
        description="The design you approve, personalized for every player who shares it."
        footer={<HelpFooter guideUrl={GUIDE_LINKS.how} onClose={() => onOpenChange(false)} />}
      >
        <HelpCards items={STEPS} columns={2} numbered />
        <HelpCallout>
          The design you approve is the design players get. Layout, text, colors, and logo are locked in. Only the
          player's details, the review, and the background change.
        </HelpCallout>
        <HelpFaq items={FAQ} />
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-slate-500">
          <span>Also see</span>
          <Button variant="link" size="inline" onClick={() => setLinked("ask")}>
            What can I ask for?
          </Button>
          <Button variant="link" size="inline" onClick={() => setLinked("backgrounds")}>
            How backgrounds work
          </Button>
        </div>
      </HelpDialog>
      <WhatCanIAskDialog open={linked === "ask"} onOpenChange={closeLinked} />
      <HowBackgroundsWorkDialog open={linked === "backgrounds"} onOpenChange={closeLinked} />
    </>
  );
};
